import { customAttribute, inject } from "aurelia-framework";

@customAttribute("wheel-h-scroll")
@inject(Element)
export class WheelHScroll {
	private isPointerDown = false;
	private didDrag = false;
	private dragStartX = 0;
	private dragStartScrollLeft = 0;

	// Distance (px) beyond which a pointer-down/move is treated as a drag
	// rather than a click, so tab click handlers still work normally.
	private static readonly DragThreshold = 5;

	constructor(private element: HTMLElement) {
		this.handleWheel = this.handleWheel.bind(this);
		this.handlePointerDown = this.handlePointerDown.bind(this);
		this.handlePointerMove = this.handlePointerMove.bind(this);
		this.handlePointerUp = this.handlePointerUp.bind(this);
		this.handleClickCapture = this.handleClickCapture.bind(this);
		this.handleDragStart = this.handleDragStart.bind(this);
	}

	attached() {
		this.element.addEventListener("wheel", this.handleWheel, { passive: false });
		this.element.addEventListener("pointerdown", this.handlePointerDown);
		this.element.addEventListener("click", this.handleClickCapture, true);
		// Anchors/images inside the tab strip are natively draggable, which
		// otherwise hijacks the mouse gesture before pointermove can fire.
		this.element.addEventListener("dragstart", this.handleDragStart);
	}

	detached() {
		this.element.removeEventListener("wheel", this.handleWheel);
		this.element.removeEventListener("pointerdown", this.handlePointerDown);
		this.element.removeEventListener("click", this.handleClickCapture, true);
		this.element.removeEventListener("dragstart", this.handleDragStart);
		this.teardownDragListeners();
	}

	handleDragStart(e: DragEvent) {
		e.preventDefault();
	}

	handleWheel(e: WheelEvent) {
		// Only take over the wheel event when the element actually overflows
		// horizontally and the gesture is primarily vertical (a normal mouse wheel).
		const canScrollHorizontally = this.element.scrollWidth > this.element.clientWidth;
		const isPrimarilyVertical = Math.abs(e.deltaY) > Math.abs(e.deltaX);

		if (canScrollHorizontally && isPrimarilyVertical) {
			this.element.scrollLeft += e.deltaY;
			e.preventDefault();
		}
	}

	handlePointerDown(e: PointerEvent) {
		// Only mimic touch-style click-and-drag panning for mouse input;
		// touch/pen already get native scrolling.
		if (e.pointerType !== "mouse" || e.button !== 0) {
			return;
		}

		const canScrollHorizontally = this.element.scrollWidth > this.element.clientWidth;
		if (!canScrollHorizontally) {
			return;
		}

		this.isPointerDown = true;
		this.didDrag = false;
		this.dragStartX = e.clientX;
		this.dragStartScrollLeft = this.element.scrollLeft;

		// Prevent native text-selection/link-drag from hijacking the gesture
		// so pointermove keeps firing while the mouse button is held down.
		e.preventDefault();

		window.addEventListener("pointermove", this.handlePointerMove);
		window.addEventListener("pointerup", this.handlePointerUp);
		window.addEventListener("pointercancel", this.handlePointerUp);
	}

	handlePointerMove(e: PointerEvent) {
		if (!this.isPointerDown) {
			return;
		}

		const delta = e.clientX - this.dragStartX;

		if (!this.didDrag && Math.abs(delta) > WheelHScroll.DragThreshold) {
			this.didDrag = true;
			this.element.classList.add("wheel-h-scroll-dragging");
		}

		if (this.didDrag) {
			this.element.scrollLeft = this.dragStartScrollLeft - delta;
			e.preventDefault();
		}
	}

	handlePointerUp() {
		this.isPointerDown = false;
		this.element.classList.remove("wheel-h-scroll-dragging");
		this.teardownDragListeners();
		// didDrag is reset on the next pointerdown; the click-capture
		// handler still needs it to suppress the upcoming click event.
	}

	handleClickCapture(e: MouseEvent) {
		// Prevent the click that follows a drag-release from triggering
		// tab selection or navigation.
		if (this.didDrag) {
			e.preventDefault();
			e.stopPropagation();
			this.didDrag = false;
		}
	}

	private teardownDragListeners() {
		window.removeEventListener("pointermove", this.handlePointerMove);
		window.removeEventListener("pointerup", this.handlePointerUp);
		window.removeEventListener("pointercancel", this.handlePointerUp);
	}
}
